/**
 * Registre central de tous les templates de prise de besoin
 * Les templates sont définis dans le code pour faciliter la versioning et les modifications via Cursor
 */

import type { IntakeTemplate } from '../templateTypes';
import { templatePdtiStandardPme } from './pdti-standard-pme';
import { templatePdtiPmeV2 } from './pdti-pme-v2';
import { templatePdtiPmeV3 } from './pdti-pme-v3';
import { templatePolitiqueCybersecuriteV2 } from './politique-cybersecurite-v2';
import { templateRapportIncidentV1 } from './rapport-incident-v1';
import { templatePolitiqueIaV1 } from './politique-ia-v1';
import { templatePolitiqueGestionIncidentsV1 } from './politique-gestion-incidents-v1';
import { templatePolitiqueSecuriteInformationV1 } from './politique-securite-information-v1';
import { templatePolitiqueClassificationIncidentsV1 } from './politique-classification-incidents-v1';
import { templatePolitiqueEscaladeIncidentsV1 } from './politique-escalade-incidents-v1';
import { templatePlanInterventionIncidentV1 } from './plan-intervention-incident-v1';
import { templatePlanIncidentMajeurV1 } from './plan-incident-majeur-v1';
import { templatePlanCommunicationIncidentV1 } from './plan-communication-incident-v1';
import { templatePlanContinuiteTiV1 } from './plan-continuite-ti-v1';
import { templatePlanIncidentViePriveeV1 } from './plan-incident-vie-privee-v1';
import { templateProcedureGestionIncidentsV1 } from './procedure-gestion-incidents-v1';
import { templateProcedureReponseIncidentsSecuriteV1 } from './procedure-reponse-incidents-securite-v1';
import { templateProcedureEscaladeV1 } from './procedure-escalade-v1';
import { templateProcedureGestionPreuvesV1 } from './procedure-gestion-preuves-v1';
import { templatePostIncidentReviewV1 } from './post-incident-review-v1';
import { templateAfterActionReportV1 } from './after-action-report-v1';
import { templateRapportLeconsApprisesV1 } from './rapport-lecons-apprises-v1';
import { templatePlanDirecteurTechnique } from './plan-directeur-technique-v1';
import { templatePlanBudgetaire5AnsV1 } from './plan-budgetaire-5ans-v1';
import { templateOdsSupportGestion } from './ods-support-gestion';
import { templatePriseDeBesoinClient2026 } from './prise-de-besoin-client-2026';
import { templatePriseDeBesoinClient2026Analyse } from './prise-de-besoin-client-2026-analyse';
import { templatePrismeKmiV1 } from './prisme-kmi-v1';
import { templatePrismeKmiCibleV1 } from './prisme-kmi-cible-v1';

// Exporter tous les templates
export const INTAKE_TEMPLATES: IntakeTemplate[] = [
  templatePdtiStandardPme,
  templatePdtiPmeV2,
  templatePdtiPmeV3,
  templatePlanDirecteurTechnique,
  templatePlanBudgetaire5AnsV1,
  templatePriseDeBesoinClient2026,
  templatePriseDeBesoinClient2026Analyse,
  templatePrismeKmiV1,
  templatePrismeKmiCibleV1,
  templatePolitiqueCybersecuriteV2,
  templateRapportIncidentV1,
  templatePolitiqueIaV1,
  templatePolitiqueGestionIncidentsV1,
  templatePolitiqueSecuriteInformationV1,
  templatePolitiqueClassificationIncidentsV1,
  templatePolitiqueEscaladeIncidentsV1,
  templatePlanInterventionIncidentV1,
  templatePlanIncidentMajeurV1,
  templatePlanCommunicationIncidentV1,
  templatePlanContinuiteTiV1,
  templatePlanIncidentViePriveeV1,
  templateProcedureGestionIncidentsV1,
  templateProcedureReponseIncidentsSecuriteV1,
  templateProcedureEscaladeV1,
  templateProcedureGestionPreuvesV1,
  templatePostIncidentReviewV1,
  templateAfterActionReportV1,
  templateRapportLeconsApprisesV1,
  templateOdsSupportGestion,
  // Ajouter d'autres templates ici au fur et à mesure
  // Ex: templatePdtiLargeEntreprise, templateOdsSupport, etc.
];

/**
 * Récupère un template par son ID
 */
export function getIntakeTemplate(templateId: string): IntakeTemplate | undefined {
  return INTAKE_TEMPLATES.find((t) => t.id === templateId);
}

/**
 * Récupère tous les templates pour un type de document
 */
export function getIntakeTemplatesByDocumentType(documentTypeCode: string): IntakeTemplate[] {
  if (documentTypeCode === 'OFFRE_SERVICE_BZ') {
    return INTAKE_TEMPLATES.filter((t) =>
      ['pdti-pme-v3', 'pdti-pme-v2', 'pdti-standard-pme', 'ods-support-gestion'].includes(t.id)
    );
  }

  if (documentTypeCode === 'EBAUCHE_ODS') {
    return INTAKE_TEMPLATES.filter((t) =>
      ['ods-support-gestion', 'prise-de-besoin-client-2026', 'prisme-kmi-v1'].includes(t.id)
    );
  }

  if (documentTypeCode === 'PRISE_BESOIN_CLIENT_2026_ANALYSE') {
    return INTAKE_TEMPLATES.filter((t) =>
      ['prise-de-besoin-client-2026'].includes(t.id)
    );
  }

  if (documentTypeCode === 'PLAN_DIRECTEUR') {
    return INTAKE_TEMPLATES.filter((t) =>
      ['pdti-standard-pme', 'pdti-pme-v2', 'pdti-pme-v3', 'prise-de-besoin-client-2026'].includes(t.id)
    );
  }

  if (documentTypeCode === 'PLAN_DIRECTEUR_5_ANS_V2') {
    return INTAKE_TEMPLATES.filter((t) =>
      ['prisme-kmi-v1', 'prise-de-besoin-client-2026'].includes(t.id)
    );
  }

  // Certains livrables utilisent la même prise de besoin.
  const normalized =
    documentTypeCode;
  return INTAKE_TEMPLATES.filter((t) => t.documentTypeCode === normalized);
}

